"use client";

import {
  type ValuesType,
  createZipCodeLookup,
  determineSaveValues,
} from "@packages/server/src/smarty/autocomplete";
import { useField, useFormikContext } from "formik";

import {
  ZIP_CODE_ERROR_MESSAGE,
  setUserInteractionDataAttribute,
} from "@shared/constants";

import kebabToCamelCase from "@shared/functions/kebabToCamelCase";
import { logError, logWarning } from "@shared/functions/log";
import sleep from "@shared/functions/sleep";

import postEvent from "@server/front-end-api/postEvent";

import Field from "@client/components/formik/field";
import Input from "@client/components/formik/input";
import Zip from "@client/components/formik/zip-code";
import Fieldset from "@client/components/lead-form/components/fieldset";

import { FormButton } from "@ui/js/components/buttons/form-button";

const INPUT_ID_AND_LABEL_HTMLFOR = "zip-code";
const FIELD_NAME_SELECTOR = kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR);

export default function ZipCode() {
  const { setFieldValue, setStatus, submitCount, submitForm, values } =
    useFormikContext();

  const formValues = values as ValuesType;
  // Grabs previous error message from previous submit for addressValidation function -- must be outside function because hooks can only be called top level
  const [zipCodeField, zipCodeMeta] = useField(FIELD_NAME_SELECTOR);

  // If the previous submit had an invalid formatted address
  const previousSubmitHadInvalidFormat =
    zipCodeMeta.error === ZIP_CODE_ERROR_MESSAGE;

  function onSubmitZipCode() {
    createZipCodeLookup(formValues.zipCode)
      .then((result) => {
        if (result.lookups) {
          return result?.lookups[0]?.result[0];
        } else {
          logError({
            message: `Smarty API not responding`,
            error: result.error,
          });
          return "";
        }
      })
      .then((result) => {
        setFieldValue("zipCodeValid", result.valid);
        const saveValues = result.valid
          ? determineSaveValues(
              result.zipcodes[0].alternateCounties,
              result.cities,
            )
          : false;

        if (saveValues) {
          setFieldValue("city", result.cities[0].city);
          setFieldValue("state", result.cities[0].stateAbbreviation);
        }
        if (result.valid === false) {
          logWarning({
            message: `Zip code provided is not valid, submitCount: ${submitCount}`,
            error: "smarty_validation",
          });
          postEvent("warning", {
            error: {
              error_message: "Zip code provided is not valid",
              error_object: "Lead Form",
              error_type: "smarty_validation",
              path: window.location.href,
            },
          });
        }
      })
      .then(() => sleep(300))
      .then(() => submitForm());
  }

  async function zipCodeValidation() {
    // The error message to display to the user
    let errorMessage;

    // If submit count is <0 and the previous submit did not have an invalid format, do not show validation error and let field be submitted
    if (
      submitCount > 0 &&
      !previousSubmitHadInvalidFormat &&
      formValues.zipCode
    ) {
      if (formValues.zipCodeValid == false) {
        logWarning({
          message: `User has successfully submitted a zip code not verified by Smarty, submitCount: ${submitCount}`,
          error: "smarty_validation",
        });
        postEvent("warning", {
          error: {
            error_message: `User has successfully submitted a zip code not verified by Smarty`,
            error_object: "Lead Form",
            error_type: "smarty_validation",
            path: window.location.href,
            invalid_address: formValues.oneLineAddress,
          },
        });
        return ""; // No validation error
      }
      if (!formValues.zipCodeValid) {
        setStatus("warning");
        errorMessage =
          "The entered 5-digit zip code appears to be invalid.  Please verify it is correct.";
      }
    }

    // Reset the lead form status via Formik
    setStatus("");

    // If the zip code is valid, return no error
    // If zipCodeValid is undefined, return no error because the smarty api is down and we want the user to proceed to the next step
    if (formValues.zipCodeValid || formValues.zipCodeValid === undefined) {
      return "";
    } else if (!formValues.zipCode) {
      // Set the lead form status to "error" via Formik
      setStatus("error");

      logWarning({
        message: `User has submitted a blank zip code form`,
        error: "smarty_validation",
      });
      postEvent("warning", {
        error: {
          error_message: `User has submitted a blank zip code form`,
          error_object: "Lead Form",
          error_type: "smarty_validation",
          path: window.location.href,
        },
      });

      errorMessage = ZIP_CODE_ERROR_MESSAGE;
      return errorMessage;
    } else if (formValues.zipCodeValid === false) {
      // Set the lead form status to "warning" via Formik
      setStatus("warning");

      logWarning({
        message: `User has submitted a zip code not verified by Smarty; warning message shown`,
        error: "smarty_validation",
      });
      postEvent("warning", {
        error: {
          error_message: `User has submitted a zip code not verified by Smarty; warning message shown`,
          error_object: "Lead Form",
          error_type: "smarty_validation",
          path: window.location.href,
          invalid_address: formValues.oneLineAddress,
        },
      });

      errorMessage =
        "The entered 5-digit zip code appears to be invalid.  Please verify it is correct.";
      await sleep(300);
    }

    return errorMessage;
  }

  return (
    <>
      <Input type="hidden" name="city" id="city" />
      <Input type="hidden" name="state" id="state" />
      <Input type="hidden" name="zipCodeValid" id="zip-code-valid" />
      <Fieldset
        dialog="We ask for your zip code to consider regional factors in your debt consolidation choices. Different areas have different lending rules, rates, and services. Your zip code helps us give you advice and options that match your location's financial landscape, making our recommendations more relevant to you."
        legend="What is your zip code?"
      >
        <Field
          {...setUserInteractionDataAttribute(
            `lead-form_step_${INPUT_ID_AND_LABEL_HTMLFOR}`,
          )}
          id={INPUT_ID_AND_LABEL_HTMLFOR}
          name={FIELD_NAME_SELECTOR}
          label="Zip Code"
          validate={zipCodeValidation}
        >
          <Zip
            id={INPUT_ID_AND_LABEL_HTMLFOR}
            name={FIELD_NAME_SELECTOR}
            onKeyDown={(e: React.KeyboardEvent) => {
              // if key is Enter preventDefault
              if (e.key === "Enter") {
                e.preventDefault();
                onSubmitZipCode();
              }
            }}
          />
        </Field>
      </Fieldset>
      <FormButton // This is necessary to prevent default form submission behavior
        {...setUserInteractionDataAttribute(
          `lead-form_submit-button_${INPUT_ID_AND_LABEL_HTMLFOR}`,
        )}
        type="button"
        onClick={onSubmitZipCode}
      />
    </>
  );
}
